import {Label, openDeleteDialog, UploadImage} from 'platform/components';
import {HStack, VStack} from 'platform/foundation';

import {head, isNil, isNotNil} from 'ramda';

import {useGetFileByIdQuery, useUploadFileMutation} from '@dms/api/base';
import {
  useClearTenantValueWithoutReferenceMutation,
  useGetTenantSettingValuesQuery,
  useSetTenantSettingValueMutation,
} from '@dms/api/settings';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

export function TenantSection() {
  const [uploadFile] = useUploadFileMutation();
  const [uploadTenantImage] = useSetTenantSettingValueMutation();
  const [clearTenantImage] = useClearTenantValueWithoutReferenceMutation();

  const {data: emailSettings} = useGetTenantSettingValuesQuery({settingId: 'tenant-logo_email'});
  const {data: documentSettings} = useGetTenantSettingValuesQuery({
    settingId: 'tenant-logo_document',
  });
  const {data: onlineOfferSettings} = useGetTenantSettingValuesQuery({
    settingId: 'tenant-logo_digital-deal',
  });

  const documentImageFileId = head(documentSettings ?? [])?.value?.fileId;
  const emailImageFileId = head(emailSettings ?? [])?.value?.fileId;
  const onlineOfferImageFileId = head(onlineOfferSettings ?? [])?.value?.fileId;

  const {data: documentImage} = useGetFileByIdQuery(
    {fileId: documentImageFileId},
    {skip: isNil(documentImageFileId)}
  );
  const {data: emailImage} = useGetFileByIdQuery(
    {fileId: emailImageFileId},
    {skip: isNil(emailImageFileId)}
  );
  const {data: onlineOfferImage} = useGetFileByIdQuery(
    {fileId: onlineOfferImageFileId},
    {skip: isNil(onlineOfferImageFileId)}
  );

  const handleUploadImage = async (
    file: File,
    settingId: 'tenant-logo_document' | 'tenant-logo_email' | 'tenant-logo_digital-deal'
  ) => {
    const data = await uploadFile({file}).unwrap();

    await uploadTenantImage({
      settingId,
      valueRequestBody: {force: false, value: {fileId: data.fileId}},
    }).unwrap();

    return {id: data.fileId, url: data.fileUri};
  };

  const handleRemoveImage = (
    settingId: 'tenant-logo_document' | 'tenant-logo_email' | 'tenant-logo_digital-deal'
  ) => {
    openDeleteDialog({
      'data-testid': `tenantSection-delete-${settingId}`,
      onConfirm: () =>
        clearTenantImage({
          settingId,
        })
          .unwrap()
          .catch(handleApiError),
    });
  };

  const hasEmailImage = isNotNil(emailImageFileId) && isNotNil(emailImage);
  const hasDocumentImage = isNotNil(documentImageFileId) && isNotNil(documentImage);
  const hasOnlineOfferImage = isNotNil(onlineOfferImageFileId) && isNotNil(onlineOfferImage);

  return (
    <HStack spacing={8}>
      <VStack>
        <Label>{i18n.t('page.settings.labels.documentLogo')}</Label>
        <UploadImage
          aspectRatio="5/1"
          uploadFileRequest={(file) => handleUploadImage(file, 'tenant-logo_document')}
          onDeleteClick={() => handleRemoveImage('tenant-logo_document')}
          value={
            hasDocumentImage ? {id: documentImageFileId, url: documentImage?.originalUri} : null
          }
          size="minHeight"
        />
      </VStack>

      <VStack>
        <Label>{i18n.t('page.settings.labels.emailLogo')}</Label>
        <UploadImage
          aspectRatio="5/1"
          uploadFileRequest={(file) => handleUploadImage(file, 'tenant-logo_email')}
          onDeleteClick={() => handleRemoveImage('tenant-logo_email')}
          value={hasEmailImage ? {id: emailImageFileId, url: emailImage?.originalUri} : null}
          size="minHeight"
        />
      </VStack>

      <VStack>
        <Label>{i18n.t('page.settings.labels.onlineOfferLogo')}</Label>
        <UploadImage
          aspectRatio="5/1"
          uploadFileRequest={(file) => handleUploadImage(file, 'tenant-logo_digital-deal')}
          onDeleteClick={() => handleRemoveImage('tenant-logo_digital-deal')}
          value={
            hasOnlineOfferImage
              ? {id: onlineOfferImageFileId, url: onlineOfferImage?.originalUri}
              : null
          }
          size="minHeight"
        />
      </VStack>
    </HStack>
  );
}
